import React, { useMemo } from 'react';
import { InfoOutlineIcon } from '@chakra-ui/icons';
import {
  Box,
  Flex,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import {
  createCustomCellularIcon,
  createCustomEthernetIcon,
  createCustomNoneIcon,
  createCustomWifiIcon,
} from 'clipsal-cortex-icons/src/custom-dynamic-icons';

import { getStrengthValue } from '../helpers';
import { HardwareConnection } from '../types';

export function ConnectionItem({ connection }: { connection?: HardwareConnection }) {
  const { t } = useTranslation();
  const bgColor = useColorModeValue('customGrey.700', '#fff');
  const textColor = useColorModeValue('#fff', 'customGrey.700');
  const isWifi = connection?.type === 'WIFI';

  const { label, Icon } = useMemo(() => {
    const { connection_status: connectionStatus, signal_strength: signalStrength, type, enabled } = connection ?? {};
    const signalKey = connectionStatus === 'Not connected' ? connectionStatus : signalStrength ?? connectionStatus;
    const internalLabel = t(`Hardware.signalKey.${enabled ? signalKey : 'Disabled'}`);
    const strengthValue = getStrengthValue(enabled ? signalKey : undefined);
    const color = '#3DCD58';

    if (type === 'WIFI') {
      return {
        label: internalLabel,
        Icon: createCustomWifiIcon(strengthValue, color),
      };
    }
    if (type === 'CELLULAR_MODEM') {
      return {
        label: internalLabel,
        Icon: createCustomCellularIcon(strengthValue, color),
      };
    }
    if (type === 'ETHERNET') {
      return {
        label: internalLabel,
        Icon: createCustomEthernetIcon(strengthValue, color),
      };
    }
    return {
      label: internalLabel,
      Icon: createCustomNoneIcon(),
    };
  }, [connection, t]);

  const boxProps = {
    bg: '#E7E6E6',
    _dark: {
      bg: '#363E40',
    },
    mt: 1,
    py: 6,
    px: 4,
    borderBottomLeftRadius: '8px',
    borderBottomRightRadius: '39px',
    borderTopRightRadius: '8px',
    borderTopLeftRadius: '8px',
    display: 'flex',
    justifyContent: 'space-between',
    position: 'relative' as const,
  };

  return (
    <Flex direction={'column'} w="100%" data-testid="device-connection" mt={8}>
      <Text fontWeight={'semibold'}> {t('Hardware.connection')}</Text>
      <Box
        data-testid={`device-connection-strength`}
        {...boxProps}
        borderBottomLeftRadius={isWifi ? 0 : '8px'}
        borderBottomRightRadius={isWifi ? 0 : '39px'}
      >
        <Text>
          {t('Hardware.signal')}
          {label}
        </Text>
        <Icon />
        {isWifi && (
          <Box
            left={4}
            bottom={0}
            right={0}
            position={'absolute'}
            w={'calc(100% - 1rem)'}
            h="1px"
            bg={'rgba(151, 151, 151, 0.3)'}
          />
        )}
      </Box>
      {isWifi && (
        <>
          <Box
            data-testid={`device-connection-network`}
            {...boxProps}
            borderBottomLeftRadius={0}
            borderBottomRightRadius={0}
            borderTopRightRadius={0}
            borderTopLeftRadius={0}
            mt={0}
          >
            <Text>{t('Hardware.network')}</Text>
            <Text>{connection.ssid ?? ''}</Text>
            {isWifi && (
              <Box
                left={4}
                bottom={0}
                right={0}
                position={'absolute'}
                w={'calc(100% - 1rem)'}
                h="1px"
                bg={'rgba(151, 151, 151, 0.3)'}
              />
            )}
          </Box>
          <Box
            data-testid={`device-connection-info`}
            {...boxProps}
            borderTopRightRadius={0}
            borderTopLeftRadius={0}
            mt={0}
          >
            {t('Hardware.network edit')}
            <Popover>
              <PopoverTrigger>
                <IconButton
                  height={'1em'}
                  min-width={'auto'}
                  marginLeft={2}
                  size={'lg'}
                  aria-label={'info'}
                  variant={'ghost'}
                  icon={<InfoOutlineIcon />}
                  data-testid={`device-connection-info-popover-button`}
                />
              </PopoverTrigger>
              <PopoverContent
                borderRadius={8}
                w="210px"
                border="none"
                data-testid="device-connection-info-popover-content"
                bg={bgColor}
                textColor={textColor}
              >
                <PopoverBody
                  borderRadius="14px"
                  boxShadow="rgba(0, 0, 0, 0.3) 0px 0px 10px 0px"
                  paddingTop="1em"
                  fontSize={'12px'}
                >
                  {t('Hardware.info')}
                </PopoverBody>
              </PopoverContent>
            </Popover>
          </Box>
        </>
      )}
    </Flex>
  );
}
